import { Injectable } from '@angular/core';
import DxfParser, { IDxf, IEntity, ILwpolylineEntity } from 'dxf-parser';
import { SitePolygoneDto } from '../bo/site/sitePolygone';
import { HttpService } from './http.service';
import * as pako from 'pako';
import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';

export interface DxfColorInfo {
  displayColor?: string;
  hexColor?: string;
  layerName?: string;
  layerColor?: string;
  layerVisible?: boolean;
  originalColorIndex?: number;
}

export type DxfEntityWithColor = IEntity & DxfColorInfo;

@Injectable({
  providedIn: 'root'
})
export class DxfServiceService {
  private dxf: IDxf | null = null;
  public polylines: IEntity[] = [];

  constructor(private readonly httpService: HttpService) { }

  public parseDxfFile(fileContent: string): DxfEntityWithColor[] | null {
    const parser = new DxfParser();
    try {
      this.dxf = parser.parse(fileContent);
      if (this.dxf !== null) {
        const entities = this.dxf.entities.filter(ent => 
          ent.type === "LWPOLYLINE" || ent.type === "POLYLINE"
        );
        
        // Extract color information for each entity
        return entities.map(entity => this.extractColorInfo(entity));
      }
    } catch (err) {
      if (err instanceof Error) {
        console.error(err.stack);
      }
      return null;
    }
    return null;
  }

  private extractColorInfo(entity: IEntity): DxfEntityWithColor {
    const entityWithColor: DxfEntityWithColor = { ...entity };
    
    // Get layer information
    if (entity.layer && this.dxf?.tables?.layer) {
      const layer = this.dxf.tables.layer.layers[entity.layer];
      if (layer) {
        entityWithColor.layerName = entity.layer;
        entityWithColor.layerVisible = layer.visible && !layer.frozen;
        
        // Use the RGB color value from the layer
        if (layer.color !== undefined) {
          entityWithColor.layerColor = this.convertRGBToHex(layer.color);
          entityWithColor.originalColorIndex = layer.colorIndex;
        }
      }
    }
    
    // Check if entity has its own color (overrides layer color)
    if (entity.color !== undefined && entity.color !== null && entity.color !== 256) {
      // Entity has its own color (not ByLayer)
      if (typeof entity.color === 'number') {
        entityWithColor.hexColor = this.convertRGBToHex(entity.color);
      }
    } else {
      // Use layer color (ByLayer or no entity color specified)
      entityWithColor.hexColor = entityWithColor.layerColor;
    }
    
    // Default fallback color
    if (!entityWithColor.hexColor) {
      entityWithColor.hexColor = '#FFFFFF';
    }
    
    // Set display color for easy access
    entityWithColor.displayColor = entityWithColor.hexColor;
    
    return entityWithColor;
  }

  private convertRGBToHex(rgbValue: number): string {
    // Convert RGB integer to hex string
    // RGB value is stored as a single integer (e.g., 16711680 for red)
    const red = (rgbValue >> 16) & 0xFF;
    const green = (rgbValue >> 8) & 0xFF;
    const blue = rgbValue & 0xFF;
    
    return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
  }

  // Helper method to get entity display color
  public getEntityDisplayColor(entity: DxfEntityWithColor): string {
    return entity.displayColor || entity.hexColor || '#FFFFFF';
  }

  // Helper method to check if entity should be visible (based on layer visibility)
  public isEntityVisible(entity: DxfEntityWithColor): boolean {
    return entity.layerVisible !== false;
  }

  // Get all layers with their colors for UI display
  public getLayersInfo(): Array<{ name: string; color: string; visible: boolean; colorIndex: number }> {
    if (!this.dxf?.tables?.layer) {
      return [];
    }

    return Object.values(this.dxf.tables.layer).map(layer => ({
      name: layer.name,
      color: this.convertRGBToHex(layer.color),
      visible: layer.visible && !layer.frozen,
      colorIndex: layer.colorIndex
    }));
  }

  // Filter entities by layer visibility
  public getVisibleEntities(entities: DxfEntityWithColor[]): DxfEntityWithColor[] {
    return entities.filter(entity => this.isEntityVisible(entity));
  }

  // Create OpenLayers VectorLayer from DXF entities
  public createVectorLayer(entities: DxfEntityWithColor[], epsg: number): VectorLayer<VectorSource> {
    const vectorSource = new VectorSource();
    
    entities.forEach(line => {
      const polyline = line as ILwpolylineEntity & DxfEntityWithColor;
      const polylineCoords = [];
      
      for (let i = 0; i < polyline.vertices.length - 1; i++) {
        polylineCoords.push([polyline.vertices[i].x, polyline.vertices[i].y]);
      }

      const geometry = new LineString(polylineCoords);
      geometry.transform(`EPSG:${epsg}`, 'EPSG:3857');

      // Get the color from the DXF entity
      const entityColor = this.getEntityDisplayColor(polyline);

      // Create LineString feature
      const polylineFeature = new Feature<LineString>({
        geometry: geometry
      });

      // Store the color and layer information on the feature
      polylineFeature.set('color', entityColor);
      polylineFeature.set('layerName', polyline.layerName);
      polylineFeature.set('layerVisible', polyline.layerVisible);

      vectorSource.addFeature(polylineFeature);
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      // Use a style function to apply different colors to each feature
      style: (feature) => {
        const color = feature.get('color') || '#FF0000'; // Fallback to red
        return new Style({
          stroke: new Stroke({
            color: color,
            width: 2,
          }),
        });
      }
    });

    return vectorLayer;
  }

  // Compress DXF file content using pako
  public compressDxf(dxfContent: string): string {
    try {
      // Convert string to Uint8Array
      const textEncoder = new TextEncoder();
      const input = textEncoder.encode(dxfContent);
      
      // Compress using pako
      const compressed = pako.deflate(input, { level: 9 });
      
      // Convert to base64 for safe transport
      // Use a chunk-based approach to avoid stack overflow on large arrays
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < compressed.length; i += chunkSize) {
        const chunk = compressed.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const base64 = btoa(binary);
      
      return base64;
    } catch (err) {
      console.error('Error compressing DXF:', err);
      throw err;
    }
  }

  // Decompress DXF file content using pako
  public decompressDxf(compressedBase64: string): string {
    try {
      // Decode from base64
      const binaryString = atob(compressedBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Decompress using pako
      const decompressed = pako.inflate(bytes);
      
      // Convert back to string
      const textDecoder = new TextDecoder();
      return textDecoder.decode(decompressed);
    } catch (err) {
      console.error('Error decompressing DXF:', err);
      throw err;
    }
  }

  public saveToSitePolygone(siteId: number, polygon: SitePolygoneDto): Promise<any> {
    return this.httpService.post(`sites/${siteId}/sitepolygone`, polygon).toPromise();
  }

  public loadSitepolygones(siteId: number): Promise<SitePolygoneDto[]> {
    return this.httpService.get<SitePolygoneDto[]>(`sites/${siteId}/sitepolygone`).toPromise();
  }

  public deleteSitePolygone(siteId: number, sitePolygoneId: number): Promise<any> {
    return this.httpService.delete(`sites/${siteId}/sitepolygone/${sitePolygoneId}`).toPromise();
  }

  public updateSitePolygone(siteId: number, sitePolygone: SitePolygoneDto): Promise<any> {
    return this.httpService.put(`sites/${siteId}/sitepolygone/${sitePolygone.id}`, sitePolygone).toPromise();
  }

  /** Toggles a DXF layer visibility without resending the whole DXF payload */
  public setSitePolygoneVisibility(siteId: number, sitePolygoneId: number, isVisible: boolean): Promise<any> {
    return this.httpService
      .put(`sites/${siteId}/sitepolygone/${sitePolygoneId}/visibility?isVisible=${isVisible}`, {})
      .toPromise();
  }
}
